import { useMutation } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { IonCard, IonCol, IonGrid, IonRow } from "@ionic/react";
import { RefObject, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

import CircleButton from "@components/buttons/CircleButton";
import BaseErrors from "@components/fields/BaseErrors";
import Checkbox from "@components/fields/Checkbox";
import RichTextarea from "@components/fields/RichTextarea";
import Form from "@components/forms/Form";
import styles from "@components/forms/MessageForm.module.css";
import { ResourceData, WorksheetData } from "@components/messaging/Messages";
import Nbsp from "@components/Nbsp";
import BodyCopy from "@components/typography/BodyCopy";
import ExternalLink from "@components/typography/ExternalLink";
import InternalLink from "@components/typography/InternalLink";
import useCreateMessage from "@hooks/mutations/useCreateMessage";
import useBooleanToggle from "@hooks/useBooleanToggle";
import useFormPersist from "@hooks/useFormPersist";
import useMyExpert from "@hooks/useMyExpert";
import {
  ExpertRoleCreateDocument,
  ExpertRoleCreateMutation,
  ExpertRoleCreateMutationVariables,
  ExpertRoleRoleEnum,
  GetJourneyForMessagesQuery,
  MessageCtaEnum,
  MessageRecordTypeEnum,
  MessageTypingDocument,
  MessageTypingMutation,
  MessageTypingMutationVariables
} from "@typing/Generated";
import { UploadWithFile } from "@typing/Models";
import { handleMutationResponse, standardRichTextareaMenuControls } from "@utils/formUtils";
import { isIosNativeApp, isMobileWeb, isNativeApp } from "@utils/platformUtils";
import { journeyRoute } from "@utils/routeUtils";

type FormData = {
  body: string;
  consultCta: boolean;
  directUploadBlobIds?: string[];
};

type Journey = GetJourneyForMessagesQuery["journey"];
type Chat = Journey["chats"][0];

type Props = {
  chat: Chat;
  contentRef?: RefObject<HTMLIonContentElement>;
  includeCta?: boolean;
  journey: Journey;
  messagingDisabled?: boolean;
  onMessageCreate: () => void;
  resourceData: ResourceData | undefined;
  setResourceData: (resourceData: ResourceData | undefined) => void;
  setWorksheetData: (worksheetData: WorksheetData | undefined) => void;
  worksheetData: WorksheetData | undefined;
};

const MessageForm = ({
  chat,
  contentRef,
  includeCta = false,
  journey,
  messagingDisabled = false,
  onMessageCreate,
  resourceData,
  setResourceData,
  setWorksheetData,
  worksheetData
}: Props) => {
  const editorRefresher = useBooleanToggle();
  const intl = useIntl();
  const myExpert = useMyExpert();
  const [baseErrors, setBaseErrors] = useState<string[]>([]);
  const [lastTypingMessage, setLastTypingMessage] = useState<Date | undefined>(undefined);
  const [submitting, setSubmitting] = useState(false);
  const [templateId, setTemplateId] = useState<string | null>(null);

  const defaultValues: FormData = {
    body: "",
    consultCta: false,
    directUploadBlobIds: []
  };

  const typingMessageInterval = 500;

  const createMessage = useCreateMessage({ chat, journeyId: journey.id, onMessageCreate });

  const [messageTyping] = useMutation<MessageTypingMutation, MessageTypingMutationVariables>(MessageTypingDocument);

  const [createExpertRole, { client }] = useMutation<ExpertRoleCreateMutation, ExpertRoleCreateMutationVariables>(
    ExpertRoleCreateDocument,
    {
      update: (_store, { data }) => {
        if (data?.expertRoleCreate?.expertRole) {
          client.resetStore();
        }
      }
    }
  );

  const schema: yup.ObjectSchema<FormData> = yup
    .object()
    .shape({
      body: yup.string().required(intl.formatMessage({ id: "errors.message.body.blank" })),
      consultCta: yup.boolean().defined(),
      directUploadBlobIds: yup.array().defined().of(yup.string().defined())
    })
    .required();

  const form = useForm<FormData>({
    defaultValues,
    mode: "all",
    resolver: yupResolver(schema)
  });

  const formPersist = useFormPersist("messageForm", {
    form,
    onDataRestored: () => {
      editorRefresher.toggle();
    },
    uniqueId: `${journey.id}-${chat.id}`
  });

  const bodyWatch = form.watch("body");
  const uploadsWatch = form.watch("directUploadBlobIds");

  useEffect(() => {
    if (!lastTypingMessage || lastTypingMessage.getTime() < new Date().getTime() - typingMessageInterval) {
      // This isn't amazing, but I can't figure out how else to make it not indicate that the user
      // is typing when the component is initially rendered.
      if (lastTypingMessage) {
        messageTyping({ variables: { chatId: chat.id } });
      }
      setLastTypingMessage(new Date());
    }
  }, [bodyWatch, chat.id, journey, lastTypingMessage, messageTyping]);

  const scrollToBottom = useCallback(() => {
    contentRef?.current?.scrollToBottom();
  }, [contentRef]);

  const handleRemoveRecord = useCallback(() => {
    setResourceData(undefined);
    setWorksheetData(undefined);
  }, [setResourceData, setWorksheetData]);

  const onSubmit = useCallback(async () => {
    setBaseErrors([]);
    setSubmitting(true);
    const messageBody = form.getValues().body;

    if (messageBody.replace("<p></p>", "") === "") {
      form.setError("body", { message: intl.formatMessage({ id: "errors.message.body.blank" }), type: "validate" });
      setSubmitting(false);
      return;
    }

    scrollToBottom();
    return createMessage({
      body: form.getValues().body,
      chatId: chat.id,
      cta: form.getValues().consultCta ? MessageCtaEnum.SCHEDULE_CONSULT : null,
      directUploadBlobIds: form.getValues().directUploadBlobIds,
      messageTemplateId: templateId,
      recordId: resourceData?.id ?? worksheetData?.id,
      recordType: resourceData
        ? MessageRecordTypeEnum.RESOURCE
        : worksheetData
          ? MessageRecordTypeEnum.WORKSHEET
          : undefined
    }).then(response => {
      handleMutationResponse<FormData>({
        data: response?.messageCreate,
        intl,
        onSuccess: async () => {
          if (
            response?.messageCreate?.message &&
            myExpert &&
            !journey.expertRoles?.find(role => role.expert.id === myExpert.id)
          ) {
            createExpertRole({
              variables: {
                expertId: myExpert?.id,
                journeyId: journey.id,
                role: ExpertRoleRoleEnum.TEAM_MEMBER
              }
            });
          }

          formPersist.clear();
          form.reset();
          editorRefresher.toggle();
          setTimeout(scrollToBottom, 350);
        },
        setBaseErrors,
        setError: form.setError,
        txBase: "errors.message"
      });

      handleRemoveRecord();
      setSubmitting(false);
    });
  }, [
    chat.id,
    createExpertRole,
    createMessage,
    editorRefresher,
    form,
    formPersist,
    handleRemoveRecord,
    intl,
    journey.expertRoles,
    journey.id,
    myExpert,
    resourceData,
    scrollToBottom,
    templateId,
    worksheetData
  ]);

  const saveButtonDisabled = useMemo(
    () =>
      !form?.formState.isValid || form?.formState.isSubmitting || submitting || bodyWatch.replace("<p></p>", "") === "",
    [form?.formState.isSubmitting, form?.formState.isValid, submitting, bodyWatch]
  );

  const placeholder =
    isMobileWeb() || isNativeApp()
      ? intl.formatMessage({ id: "forms.messageCreate.bodyPlaceholder.mobile" })
      : intl.formatMessage({ id: "forms.messageCreate.bodyPlaceholder.desktop" });

  const handleUploadsChange = useCallback(
    (uploads: UploadWithFile[]) => {
      const blobIds = uploads.map(upload => upload.upload.signedBlobId);
      form.setValue("directUploadBlobIds", blobIds);
    },
    [form]
  );

  return (
    <Form hookForm={form} onSubmit={form.handleSubmit(onSubmit)}>
      <BaseErrors errors={baseErrors} />
      <div className={messagingDisabled ? "ion-hide" : undefined}>
        <div className={messagingDisabled ? styles.disabledArea : undefined} />
        <div className={styles.container}>
          <div className={styles.fullWidth}>
            <RichTextarea<FormData>
              controls={[
                ...standardRichTextareaMenuControls.filter(c => c !== "headings"),
                "attachments",
                "inAppLinks",
                "submit",
                "templates"
              ]}
              disabled={messagingDisabled}
              editorRefresher={editorRefresher}
              error={form.formState.errors.body}
              form={form}
              journey={journey}
              journeyId={journey.id}
              name="body"
              onUploadsChange={handleUploadsChange}
              placeholder={placeholder}
              setTemplateId={setTemplateId}
              size="small"
              submitDisabled={saveButtonDisabled}
              submitVariant="send"
              uploadsWatch={uploadsWatch}
            />
          </div>
        </div>
        <div className={styles.textArea}>
          {(resourceData || worksheetData) && (
            <IonCard className="ion-no-margin ion-margin-bottom ion-margin-top" color="gray-50">
              <IonGrid>
                <IonRow className="ion-align-items-center">
                  <IonCol>
                    <BodyCopy>
                      {worksheetData && (
                        <FormattedMessage
                          id="forms.messageCreate.worksheetInfo"
                          values={{ title: worksheetData.title }}
                        />
                      )}
                      {resourceData && (
                        <FormattedMessage
                          id="forms.messageCreate.resourceInfo"
                          values={{ category: resourceData.taskTitle, title: resourceData.title }}
                        />
                      )}
                    </BodyCopy>
                  </IonCol>
                  <IonCol size="auto">
                    <CircleButton
                      ariaLabelKey="accessibility.ariaLabels.remove"
                      icon="xmark"
                      onClick={handleRemoveRecord}
                      size="small"
                    />
                  </IonCol>
                </IonRow>
              </IonGrid>
            </IonCard>
          )}
        </div>
      </div>

      {messagingDisabled && (
        <div className="ion-margin-horizontal align-center">
          <div className={styles.hoursUsed}>
            <BodyCopy color="gray">
              <FormattedMessage id="pages.messages.hoursUsed" />
            </BodyCopy>
            <Nbsp />
            {!isIosNativeApp() && (
              <InternalLink
                to={journeyRoute({
                  journeyId: journey.id,
                  linkAfterJourney: "/account?section=package",
                  myExpert,
                  preventScroll: false
                })}
              >
                <FormattedMessage id="pages.messages.purchaseHours" />
              </InternalLink>
            )}
          </div>

          <div className={styles.questions}>
            <FormattedMessage id="pages.messages.haveAQuestion" />
            <Nbsp />
            <ExternalLink href="mailto:support@withgrayce.com">
              <FormattedMessage id="pages.messages.contactSupport" />
            </ExternalLink>
          </div>
        </div>
      )}
      {includeCta && (
        <Checkbox<FormData> control={form.control} error={form.formState.errors.consultCta} name="consultCta">
          <FormattedMessage id="forms.messageCreate.ctaLabel" />
        </Checkbox>
      )}
    </Form>
  );
};

export default MessageForm;
